import { useEffect, useMemo, useState } from "react";
import apiClient from "@/services/apiClient";
import ToastHelper from "@/utils/toastHelper";
import { Camera } from "lucide-react";

const emptyProfile = {
  firstName: "", lastName: "", email: "", mobileNumber: "", dateOfBirth: "", genderId: null,
  addressLine1: "", addressLine2: "", suburb: "", postCode: "", stateId: "", countryId: "",
  emergencyContactName: "", emergencyContactRelationshipId: null, emergencyContactNumber: "", usiNumber: "", profilePhoto: "", studentId: "",
};

export default function StudentProfile() {
  const [profile, setProfile] = useState(emptyProfile);
  const [genders, setGenders] = useState([]);
  const [emergencyRelationships, setEmergencyRelationships] = useState([]);
  const [savedProfile, setSavedProfile] = useState(emptyProfile);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const apiBaseUrl = (process.env.REACT_APP_API_URL).replace(/\/$/, "");

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const response = await apiClient.get(`${apiBaseUrl}/StudentProfile`);
        const {
          genders: genderOptions = [],
          emergencyRelationships: relationshipOptions = [],
          ...profileData
        } = response.data || {};
        setGenders(genderOptions);
        setEmergencyRelationships(relationshipOptions);
        const nextProfile = { ...emptyProfile, ...profileData, dateOfBirth: profileData.dateOfBirth?.slice(0, 10) || "" };
        setProfile(nextProfile);
        setSavedProfile(nextProfile);
      } catch (requestError) {
        console.error("Failed to load student profile", requestError);
        setError(requestError.response?.data?.message || "Unable to load your profile.");
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [apiBaseUrl]);

  const completion = useMemo(() => {
    const requiredFields = [profile.firstName, profile.lastName, profile.email, profile.mobileNumber, profile.dateOfBirth, profile.emergencyContactName, profile.emergencyContactNumber];
    return Math.round((requiredFields.filter(Boolean).length / requiredFields.length) * 100);
  }, [profile]);

  const updateField = (key, value) => setProfile((current) => ({ ...current, [key]: value }));

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...profile,
        stateId: profile.stateId === "" ? null : Number(profile.stateId),
        countryId: profile.countryId === "" ? null : Number(profile.countryId),
        dateOfBirth: profile.dateOfBirth || null,
      };
      await apiClient.put(`${apiBaseUrl}/StudentProfile`, payload);
      setSavedProfile(profile);
      ToastHelper.success("Profile updated successfully");
    } catch (requestError) {
      ToastHelper.apiError(requestError, "Unable to update your profile.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="py-12 text-center text-sm text-slate-500">Loading your profile...</div>;
  if (error) return <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>;

  const initials = `${profile.firstName?.[0] || ""}${profile.lastName?.[0] || ""}` || "S";

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="profile-form">
      <div className="wc-card p-6 flex flex-col sm:flex-row items-center sm:items-start gap-6">
        <div className="relative">
          {profile.profilePhoto ? <img src={profile.profilePhoto} alt="" className="w-28 h-28 rounded-full object-cover border-4 border-slate-100" /> : <div className="w-28 h-28 rounded-full bg-[#0A2540] text-white flex items-center justify-center text-3xl font-bold border-4 border-slate-100">{initials}</div>}
          <button type="button" onClick={() => ToastHelper.info("Profile photo upload is not available yet.")} className="absolute bottom-0 right-0 w-9 h-9 rounded-full bg-orange-500 hover:bg-orange-600 text-white flex items-center justify-center shadow-lg" data-testid="upload-photo"><Camera className="w-4 h-4" /></button>
        </div>
        <div className="flex-1 text-center sm:text-left">
          <h1 className="text-2xl font-display font-extrabold text-[#0A2540]">{profile.firstName} {profile.lastName}</h1>
          <div className="text-sm text-slate-500">Student #{profile.studentId} - USI: {profile.usiNumber || "Not supplied"}</div>
          <div className="mt-4 text-xs uppercase font-semibold text-slate-500">Profile completion</div>
          <div className="mt-1 h-2 bg-slate-100 rounded-full overflow-hidden w-full sm:w-72"><div className="h-full bg-orange-500" style={{ width: `${completion}%` }} /></div>
          <div className="text-xs text-slate-500 mt-1">{completion}% complete</div>
        </div>
      </div>

      <section className="wc-card p-6"><h2 className="text-lg font-display font-extrabold text-[#0A2540] mb-4">Personal details</h2><div className="grid sm:grid-cols-2 gap-4">
        <Field label="First name" value={profile.firstName} onChange={(value) => updateField("firstName", value)} id="pf-first" required />
        <Field label="Last name" value={profile.lastName} onChange={(value) => updateField("lastName", value)} id="pf-last" required />
        <Field label="Date of birth" value={profile.dateOfBirth} onChange={(value) => updateField("dateOfBirth", value)} id="pf-dob" type="date" />
        <SelectField label="Gender" value={profile.genderId} onChange={(value) => updateField("genderId", value === "" ? null : Number(value))} id="pf-gender" options={genders} optionValue="id" />
        <Field label="Email" value={profile.email} onChange={(value) => updateField("email", value)} id="pf-email" type="email" required />
        <Field label="Mobile" value={profile.mobileNumber} onChange={(value) => updateField("mobileNumber", value)} id="pf-phone" />
        <Field label="Address line 1" value={profile.addressLine1} onChange={(value) => updateField("addressLine1", value)} id="pf-address" full />
        <Field label="Address line 2" value={profile.addressLine2} onChange={(value) => updateField("addressLine2", value)} id="pf-address2" full />
        <Field label="Suburb" value={profile.suburb} onChange={(value) => updateField("suburb", value)} id="pf-suburb" />
        <Field label="Postcode" value={profile.postCode} onChange={(value) => updateField("postCode", value)} id="pf-postcode" />
      </div></section>

      <section className="wc-card p-6"><h2 className="text-lg font-display font-extrabold text-[#0A2540] mb-4">Emergency contact</h2><div className="grid sm:grid-cols-2 gap-4">
        <Field label="Full name" value={profile.emergencyContactName} onChange={(value) => updateField("emergencyContactName", value)} id="pf-ename" />
        <Field label="Phone" value={profile.emergencyContactNumber} onChange={(value) => updateField("emergencyContactNumber", value)} id="pf-ephone" />
        <SelectField label="Relationship" value={profile.emergencyContactRelationshipId} onChange={(value) => updateField("emergencyContactRelationshipId", value === "" ? null : Number(value))} id="pf-erel" options={emergencyRelationships} placeholder="Select relationship" optionValue="id" />
      </div></section>

      <section className="wc-card p-6"><h2 className="text-lg font-display font-extrabold text-[#0A2540] mb-4">USI</h2><Field label="Unique Student Identifier" value={profile.usiNumber} id="pf-usi" readOnly /><p className="mt-2 text-xs text-slate-500">To protect verified training records, update your USI through online enrolment.</p></section>

      <div className="flex justify-end gap-3"><button type="button" onClick={() => setProfile(savedProfile)} className="wc-btn-outline text-sm" disabled={saving}>Cancel</button><button type="submit" className="wc-btn-primary text-sm" data-testid="profile-save" disabled={saving}>{saving ? "Saving..." : "Save changes"}</button></div>
    </form>
  );
}

function Field({ label, value, onChange, id, type = "text", full = false, required = false, readOnly = false }) {
  return <div className={full ? "sm:col-span-2" : ""}><label className="wc-label">{label}</label><input type={type} className="wc-input" value={value ?? ""} onChange={(event) => onChange?.(event.target.value)} data-testid={id} required={required} readOnly={readOnly} /></div>;
}

function SelectField({ label, value, onChange, id, options, placeholder = "Select gender", optionValue = "name" }) {
  return <div><label className="wc-label">{label}</label><select className="wc-input" value={value ?? ""} onChange={(event) => onChange?.(event.target.value)} data-testid={id}><option value="">{placeholder}</option>{options.map((option) => <option key={option.id} value={option[optionValue]}>{option.name}</option>)}</select></div>;
}
